import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  articleTypesApi,
  articlesApi,
  type ArticleDetail,
  type ArticleRequest,
  type ArticleTypeView,
} from "../api/articles";
import type { ApiError } from "../api/client";
import type { AttributeDefView, AttributeValue } from "../api/catalog";
import { detailsMap, formatApiError } from "../lib/apiError";
import { unitLabel, useUnits } from "../lib/useUnits";
import { useManufacturers } from "../lib/useManufacturers";
import { AttributeValuesForm, compactAttributeValues, missingRequiredAttributes } from "./AttributeValuesForm";
import { Modal } from "./Modal";
import { PermLink } from "./PermLink";
import { Select, type SelectOption } from "./Select";

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

const ATTRIBUTE_ERRORS = new Set(["REQUIRED_ATTRIBUTE", "UNKNOWN_ATTRIBUTE", "INVALID_ATTRIBUTE_VALUE"]);

/**
 * Create (POST) and edit (PUT, full replacement) of an article (sprint 5l §3): type picked from
 * the article-type catalogue, code, name, unit and manufacturer from their codebooks, note and
 * the type's attributes through `AttributeValuesForm` (mandatory ones checked here as well, so
 * the button says why it waits). Used from `/artikli` and inline from the sastavnica line modal.
 */
export function ArticleFormModal({
  article = null, initialTypeId = null, onClose, onSaved,
}: {
  /** Edit mode when given. */
  article?: ArticleDetail | null;
  /** Create mode: the type preselected (e.g. the filter of the list the user came from). */
  initialTypeId?: string | null;
  onClose: () => void;
  onSaved: (saved: ArticleDetail) => void;
}) {
  const { t } = useTranslation();
  const editing = article;
  const { units } = useUnits();
  const { manufacturers } = useManufacturers();

  const [types, setTypes] = useState<ArticleTypeView[] | null>(null);
  const [typeId, setTypeId] = useState(editing?.typeId ?? initialTypeId ?? "");
  const [code, setCode] = useState(editing?.code ?? "");
  const [name, setName] = useState(editing?.name ?? "");
  const [unitId, setUnitId] = useState(editing?.unitId ?? "");
  const [manufacturerId, setManufacturerId] = useState(editing?.manufacturerId ?? "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [defs, setDefs] = useState<AttributeDefView[]>(editing?.attributeDefs ?? []);
  const [values, setValues] = useState<Record<string, AttributeValue>>(editing ? { ...editing.attributeValues } : {});
  const [defsLoading, setDefsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [attributeErrors, setAttributeErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    articleTypesApi.all()
      .then((list) => { if (!cancelled) setTypes(list); })
      .catch((err: ApiError) => { if (!cancelled) { setTypes([]); setError(err.message); } });
    return () => { cancelled = true; };
  }, []);

  // The attributes follow the type. The article's own definitions cover its current type (no
  // fetch, no flash); another type is fetched and the values start empty (they belong to the old defs).
  useEffect(() => {
    if (!typeId) { setDefs([]); setValues({}); return; }
    if (editing && typeId === editing.typeId) { setDefs(editing.attributeDefs); setValues({ ...editing.attributeValues }); return; }
    let cancelled = false;
    setDefsLoading(true);
    articleTypesApi.attributeDefs(typeId)
      .then((list) => { if (!cancelled) { setDefs(list); setValues({}); } })
      .catch((err: ApiError) => { if (!cancelled) { setDefs([]); setError(err.message); } })
      .finally(() => { if (!cancelled) setDefsLoading(false); });
    return () => { cancelled = true; };
  }, [typeId, editing]);

  const typeOptions = useMemo<SelectOption[]>(
    () => (types ?? []).map((x) => ({ value: x.id, label: x.name, keywords: x.code })),
    [types],
  );
  const unitOptions = useMemo<SelectOption[]>(
    () => units.map((u) => ({ value: u.id, label: unitLabel(u), keywords: u.symbol })),
    [units],
  );
  const manufacturerOptions = useMemo<SelectOption[]>(
    () => [
      { value: "", label: t("articles.manufacturer.none", "— bez proizvođača —") },
      ...manufacturers.map((m) => ({ value: m.id, label: m.name })),
    ],
    [manufacturers, t],
  );

  const missingRequired = useMemo(() => missingRequiredAttributes(defs, values), [defs, values]);
  const missingFields = [
    !typeId && t("articles.field.type", "Tip artikla"),
    !code.trim() && t("articles.field.code", "Šifra"),
    !name.trim() && t("articles.field.name", "Naziv"),
    !unitId && t("articles.field.unit", "Jedinica mere"),
  ].filter((x): x is string => typeof x === "string");
  const noTypes = types !== null && types.length === 0;

  // Every disabled control says why (2026-10-08): the submit button carries the first obstacle.
  const submitReason = submitting
    ? t("action.saving", "Čuvanje…")
    : missingFields.length > 0
      ? t("articles.form.fillIn", "Popunite: {{list}}", { list: missingFields.join(", ") })
      : missingRequired.length > 0
        ? t("articles.requiredMissing", "Obavezno: {{list}}", { list: missingRequired.map((d) => d.label).join(", ") })
        : defsLoading
          ? t("articles.form.loadingAttributes", "Učitavanje atributa tipa…")
          : null;
  const canSubmit = submitReason === null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setCodeError(null);
    setAttributeErrors({});
    const body: ArticleRequest = {
      typeId,
      code: code.trim(),
      name: name.trim(),
      unitId,
      manufacturerId: manufacturerId || null,
      attributeValues: compactAttributeValues(values),
      note: note.trim() || null,
    };
    setSubmitting(true);
    try {
      onSaved(editing ? await articlesApi.update(editing.id, body) : await articlesApi.create(body));
    } catch (err) {
      const ae = err as ApiError;
      const message = translateArticleError(t, ae, defs);
      if (ae.code === "CODE_TAKEN") {
        setCodeError(message);
      } else {
        const key = detailsMap(ae)?.key;
        if (key && ATTRIBUTE_ERRORS.has(ae.code)) setAttributeErrors({ [key]: message });
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const noTypesReason = t("articles.form.noTypes", "Nema nijednog tipa artikla — prvo napravite tip artikla.");

  return (
    <Modal
      title={editing ? t("articles.modal.editTitle", "Izmena artikla") : t("articles.modal.createTitle", "Nov artikal")}
      subtitle={editing ? <span style={{ fontFamily: "var(--mono)" }}>{editing.code}</span> : t("articles.modal.createSub", "Artikal je količinska stavka kataloga — bez QR koda i stanja. Vrednosti atributa se unose jednom, ovde; sastavnica pozicije bira artikal i upisuje samo količinu.")}
      onClose={onClose}
      width={640}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label className="field">
          <span>{t("articles.field.type", "Tip artikla")} <span className="req-mark">*</span></span>
          <div title={noTypes ? noTypesReason : undefined} data-tip={noTypes ? noTypesReason : undefined}>
            <Select
              value={typeId}
              options={typeOptions}
              onChange={setTypeId}
              disabled={types === null || noTypes}
              placeholder={types === null ? t("common.loading", "Učitavanje…") : t("articles.type.pick", "Izaberite tip artikla…")}
              ariaLabel={t("articles.field.type", "Tip artikla")}
            />
          </div>
          {noTypes && (
            <span className="muted" style={{ fontSize: 12 }}>
              {noTypesReason}{" "}
              <PermLink permission="article-types:create" to="/tipovi/artikli/novi">{t("articleTypes.new", "Nov tip artikla")}</PermLink>
            </span>
          )}
        </label>
        <div className="form-grid">
          <label className="field">
            <span>{t("articles.field.code", "Šifra")} <span className="req-mark">*</span></span>
            <input
              className={codeError ? "input is-invalid" : "input"}
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeError(null); }}
              required
              maxLength={40}
              placeholder="LEZ-6205-2RS"
              autoCapitalize="characters"
              spellCheck={false}
              style={{ fontFamily: "var(--mono)" }}
            />
            {codeError && <small className="field-error" role="alert">{codeError}</small>}
          </label>
          <label className="field">
            <span>{t("articles.field.name", "Naziv")} <span className="req-mark">*</span></span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} placeholder={t("articles.field.namePlaceholder", "npr. Ležaj 6205-2RS")} />
          </label>
        </div>
        <div className="form-grid">
          <label className="field">
            <span>{t("articles.field.unit", "Jedinica mere")} <span className="req-mark">*</span></span>
            <Select
              value={unitId}
              options={unitOptions}
              onChange={setUnitId}
              placeholder={t("articles.unit.pick", "Izaberite jedinicu…")}
              ariaLabel={t("articles.field.unit", "Jedinica mere")}
            />
          </label>
          <label className="field">
            <span>{t("articles.field.manufacturer", "Proizvođač")}</span>
            <Select
              value={manufacturerId}
              options={manufacturerOptions}
              onChange={setManufacturerId}
              ariaLabel={t("articles.field.manufacturer", "Proizvođač")}
            />
          </label>
        </div>
        {typeId && (defs.length > 0 ? (
          <div className="field">
            <span>{t("articles.section.typeAttributes", "Atributi tipa")}{missingRequired.length > 0 && <span className="req-mark"> *</span>}</span>
            <AttributeValuesForm defs={defs} values={values} disabled={submitting} onChange={(next) => { setValues(next); setAttributeErrors({}); }} errors={attributeErrors} />
            {missingRequired.length > 0 && (
              <span className="muted" style={{ fontSize: 12 }}>
                {t("articles.requiredMissing", "Obavezno: {{list}}", { list: missingRequired.map((d) => d.label).join(", ") })}
              </span>
            )}
          </div>
        ) : !defsLoading && (
          <span className="muted" style={{ fontSize: 12.5 }}>{t("articles.attributesEmpty", "Tip artikla nema definisane atribute.")}</span>
        ))}
        <label className="field">
          <span>{t("articles.field.note", "Napomena")}</span>
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} style={textarea} />
        </label>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <span className="tip-wrap" data-tip={submitReason ?? undefined} title={submitReason ?? undefined}>
            <button type="submit" className="btn primary" disabled={!canSubmit}>
              {submitting ? t("action.saving", "Čuvanje…") : editing ? t("action.save", "Sačuvaj") : t("action.create", "Kreiraj")}
            </button>
          </span>
        </div>
      </form>
    </Modal>
  );
}

/** Serbian text for an article write error; attribute keys are shown as their labels when `defs` knows them. */
export function translateArticleError(t: Translate, ae: ApiError, defs: AttributeDefView[] = []): string {
  const details = detailsMap(ae);
  const key = details?.key ?? "";
  const label = defs.find((d) => d.key === key)?.label ?? key;
  switch (ae.code) {
    case "CODE_TAKEN": return t("articles.error.codeTaken", "Šifra je već zauzeta.");
    case "REQUIRED_ATTRIBUTE": return t("articles.error.requiredAttribute", "Obavezan atribut nije unet: {{key}}", { key: label });
    case "UNKNOWN_ATTRIBUTE": return t("articles.error.unknownAttribute", "Nepoznat atribut: {{key}}", { key: label });
    case "INVALID_ATTRIBUTE_VALUE": return t("articles.error.invalidAttributeValue", "Neispravna vrednost atributa: {{key}}", { key: label });
    case "VALIDATION":
      if (details?.typeId) return t("articles.error.typeNotFound", "Izabrani tip artikla više ne postoji.");
      if (details?.unitId) return t("articles.error.unitNotFound", "Izabrana jedinica više ne postoji u šifarniku — izaberite drugu.");
      if (details?.manufacturerId) return t("articles.error.manufacturerNotFound", "Izabrani proizvođač više ne postoji u šifarniku — izaberite drugog.");
      return formatApiError(ae);
    default: return formatApiError(ae);
  }
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
